import { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Alert, Box, Button, LinearProgress, Paper, Stack, Typography } from '../../ui/core';
import { surfaceCardSx as cardSx, cardHeaderSx, cardBodySx } from '../../ui/styles';
import api from '../../lib/api';
import useSdpFetch from './useSdpFetch';
import ManualPolicyChangeForm from './ManualPolicyChangeForm';
import ResetDialog from './ResetDialog';
import { EnumPill, JsonBlock, Muted } from './parts';
import { SOURCE_PALETTES, formatTs } from './sdpFormatters';

const LIST_LIMIT = 50;

const PolicyChangesTable = ({ entries }) =>
  entries.length === 0 ? (
    <Muted>No policy changes.</Muted>
  ) : (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Effective at</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Origin</TableCell>
            <TableCell>Hard reset</TableCell>
            <TableCell>Affects</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Evidence</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((pc) => (
            <TableRow key={pc.id} data-testid={`pc-row-${pc.id}`}>
              <TableCell>{formatTs(pc.effectiveAt)}</TableCell>
              <TableCell>
                <EnumPill value={pc.source} palettes={SOURCE_PALETTES} />
              </TableCell>
              <TableCell>
                <Typography sx={{ fontSize: '13px' }}>{pc.origin}</Typography>
                {pc.kind && (
                  <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                    {pc.kind}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {pc.hardReset ? (
                  <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: 'error.main' }}>
                    hard reset
                  </Typography>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>{pc.affectsName || pc.affects}</TableCell>
              <TableCell sx={{ maxWidth: 320 }}>{pc.description}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: '13px' }}>{pc.createdBy || '—'}</Typography>
                <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                  {formatTs(pc.createdAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <JsonBlock value={pc.evidence} label="evidence" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

const PolicyChangesTab = ({ cities = [], onChanged }) => {
  const { data, loading, error, refetch } = useSdpFetch(
    () => api.supplyDemandPace.getPolicyChanges({ limit: LIST_LIMIT }),
    'policy-changes',
  );
  const [resetOpen, setResetOpen] = useState(false);

  const afterWrite = async () => {
    await refetch();
    onChanged && onChanged();
  };

  return (
    <Stack spacing="16px">
      <Paper elevation={0} sx={cardSx}>
        <Box sx={{ ...cardHeaderSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Add a manual policy change</Typography>
          <Button size="small" variant="outlined" color="error" onClick={() => setResetOpen(true)}>
            Reset…
          </Button>
        </Box>
        <Box sx={cardBodySx}>
          <ManualPolicyChangeForm cities={cities} onCreated={afterWrite} />
        </Box>
      </Paper>
      <Paper elevation={0} sx={cardSx}>
        <Box sx={cardHeaderSx}>
          <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Policy changes</Typography>
        </Box>
        <Box sx={cardBodySx}>
          {loading && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}
          {data && <PolicyChangesTable entries={data.entries || []} />}
        </Box>
      </Paper>
      <ResetDialog open={resetOpen} cities={cities} onClose={() => setResetOpen(false)} onReset={afterWrite} />
    </Stack>
  );
};

export default PolicyChangesTab;
